/** Quick-start code. `changed` marks the lines a user edits to switch to LowCostRouter. */

export interface SnippetLine {
  text: string;
  changed?: true;
}

export interface Snippet {
  id: string;
  label: string;
  /** File name shown above the code. */
  file: string;
  lines: readonly SnippetLine[];
}

const plain = (...texts: string[]): SnippetLine[] => texts.map((text) => ({ text }));
const changed = (...texts: string[]): SnippetLine[] =>
  texts.map((text) => ({ text, changed: true }));

export const SNIPPETS: readonly Snippet[] = [
  {
    id: 'openai-python',
    label: 'OpenAI Python',
    file: 'main.py',
    lines: [
      ...plain('from openai import OpenAI', '', 'client = OpenAI('),
      ...changed('    base_url="https://api.lowcostrouter.com/v1",', '    api_key="YOUR_API_KEY",'),
      ...plain(
        ')',
        '',
        'reply = client.chat.completions.create(',
        '    model="claude-sonnet-5-5",',
        '    messages=[{"role": "user", "content": "Hello!"}],',
        ')',
        'print(reply.choices[0].message.content)',
      ),
    ],
  },
  {
    id: 'openai-node',
    label: 'OpenAI Node.js',
    file: 'index.mjs',
    lines: [
      ...plain('import OpenAI from "openai";', '', 'const client = new OpenAI({'),
      ...changed(
        '  baseURL: "https://api.lowcostrouter.com/v1",',
        '  apiKey: process.env.LOWCOSTROUTER_API_KEY,',
      ),
      ...plain(
        '});',
        '',
        'const reply = await client.chat.completions.create({',
        '  model: "gpt-6-sol",',
        '  messages: [{ role: "user", content: "Hello!" }],',
        '});',
        'console.log(reply.choices[0].message.content);',
      ),
    ],
  },
  {
    id: 'anthropic-python',
    label: 'Anthropic Python',
    file: 'main.py',
    lines: [
      ...plain('import anthropic', '', 'client = anthropic.Anthropic('),
      ...changed('    base_url="https://api.lowcostrouter.com",', '    api_key="YOUR_API_KEY",'),
      ...plain(
        ')',
        '',
        'message = client.messages.create(',
        '    model="claude-opus-5-5",',
        '    max_tokens=1024,',
        '    messages=[{"role": "user", "content": "Hello!"}],',
        ')',
        'print(message.content[0].text)',
      ),
    ],
  },
  {
    id: 'gemini-python',
    label: 'Gemini Python',
    file: 'main.py',
    lines: [
      ...plain('from google import genai', '', 'client = genai.Client('),
      ...changed(
        '    api_key="YOUR_API_KEY",',
        '    http_options={"base_url": "https://api.lowcostrouter.com"},',
      ),
      ...plain(
        ')',
        '',
        'response = client.models.generate_content(',
        '    model="gemini-3.8-flash",',
        '    contents="Hello!",',
        ')',
        'print(response.text)',
      ),
    ],
  },
  {
    id: 'curl',
    label: 'cURL',
    file: 'request.sh',
    lines: [
      ...changed(
        'curl https://api.lowcostrouter.com/v1/chat/completions \\',
        '  -H "Authorization: Bearer $LOWCOSTROUTER_API_KEY" \\',
      ),
      ...plain(
        '  -H "Content-Type: application/json" \\',
        '  -d \'{"model": "grok-4.7", "messages": [{"role": "user", "content": "Hello!"}]}\'',
      ),
    ],
  },
];

export const snippetText = (snippet: Snippet): string =>
  snippet.lines.map((line) => line.text).join('\n');
